import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { addDays } from "date-fns";
import { useFieldValue, useFormikContext } from "formik";
import { max } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SelectableValue } from "../models/common/selectable-value";
import { CADataPoints } from "../models/flexible/ca-data-points";
import { ControlState } from "../models/flexible/control-state";
import { DataControllerProps, FlexControlProps } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { Case, CoverageRequestRating, CoverageRequestTherapyDecision, Maybe } from "../models/generated/graphql";
import formatValidDate from "../utils/format-valid-date";
import { getCoverageBoundaries } from "../utils/negotiation-rounds";
import { GeneralFieldController } from "./general-field-controller";
import { getEnumValues } from "../utils/get-enum-values";

function CoveragePeriodRelatedEffect(props: {
	cases: Array<Omit<Case, "flexEntities"> & { flexEntities: Array<TypedFlexEntity<any>> }>;
	controller: FlexSectionController;
}) {
	const { setFieldValue, values } = useFormikContext<CADataPoints>();
	const isPrevForm = (data: { formCreatedOn: Date | string }) =>
		!props.controller.context.flexEntity?.createdOn || data.formCreatedOn < props.controller.context.flexEntity?.createdOn;
	const isSameForm = (data: { formCreatedOn: Date | string }) =>
		!props.controller.context.flexEntity?.createdOn || data.formCreatedOn === props.controller.context.flexEntity?.createdOn;
	const coverageBoundaries = props.cases.flatMap(getCoverageBoundaries);

	const getTherapyTrialEndDate = () =>
		coverageBoundaries
			.filter(isPrevForm)
			.map((boundaries) => boundaries.decision === CoverageRequestTherapyDecision.Trial && boundaries.endDate)
			.find(Boolean);

	const getCoverageStartDate = () => {
		if (props.controller.context.flexEntity)
			return coverageBoundaries
				.filter(isSameForm)
				.map((boundaries) => boundaries.decision === CoverageRequestTherapyDecision.Approved && boundaries.startDate)
				.find(Boolean);

		const maxCoverageBoundary = max(coverageBoundaries.filter(isPrevForm).map((boundaries) => boundaries.endDate));
		return maxCoverageBoundary && addDays(maxCoverageBoundary, 1);
	};

	const setFieldIfExists = (identifier: AttributeIdentifiers, value: any) => {
		if (props.controller.fieldControllers.some((field) => field.schema.attribute.identifier === identifier)) {
			setFieldValue(identifier, value);
		}
	};

	useEffect(() => {
		if (!values.decisionTherapy) {
			return;
		}

		if (values.decisionTherapy === CoverageRequestTherapyDecision.Declined) {
			setFieldIfExists(AttributeIdentifiers.TherapyTrialStartDate, "");
			setFieldIfExists(AttributeIdentifiers.TherapyTrialEndDate, "");
			setFieldIfExists(AttributeIdentifiers.CoverageStartDate, "");
			setFieldIfExists(AttributeIdentifiers.CoverageEndDate, "");
			return;
		}

		const therapyTrialEndDate = getTherapyTrialEndDate();
		if (values.decisionTherapy === CoverageRequestTherapyDecision.Trial) {
			if (therapyTrialEndDate) {
				setFieldIfExists(AttributeIdentifiers.TherapyTrialStartDate, therapyTrialEndDate);
			}

			setFieldIfExists(AttributeIdentifiers.CoverageStartDate, "");
			setFieldIfExists(AttributeIdentifiers.CoverageEndDate, "");
			return;
		}

		const coverageStartDate = getCoverageStartDate();
		if (coverageStartDate) {
			setFieldIfExists(AttributeIdentifiers.CoverageStartDate, formatValidDate(coverageStartDate));
		}

		setFieldIfExists(AttributeIdentifiers.TherapyTrialStartDate, "");
		setFieldIfExists(AttributeIdentifiers.TherapyTrialEndDate, "");
	}, [values.decisionTherapy]);

	return null;
}

export class DecisionTherapyFieldController extends GeneralFieldController {
	override mapInitialValue(value: unknown): unknown {
		return this.parent.context.isEditable && this.isPreviouslyApproved() ? CoverageRequestTherapyDecision.Approved : value;
	}

	override useControlState(_path: string): ControlState {
		return "enabled";
	}

	override useControlProps(): Maybe<FlexControlProps> | undefined {
		const { t } = useTranslation();

		const decisionOptions = getEnumValues(CoverageRequestTherapyDecision).map((v) => ({
			label: t(`decisions-${v}`),
			value: v,
		})) as Array<SelectableValue>;
		const [rating] = useFieldValue<CoverageRequestRating>(AttributeIdentifiers.Rating);

		const coverageBoundaries = this.parent.context.cases
			.flatMap(getCoverageBoundaries)
			.filter(
				(boundaries) =>
					!this.parent.context.flexEntity?.createdOn || boundaries.formCreatedOn < this.parent.context.flexEntity?.createdOn
			);
		const isPreviousDecisionApproved = coverageBoundaries?.some((data) => data.decision === CoverageRequestTherapyDecision.Approved);

		function filterDecisionOptionsInForm(options: Array<SelectableValue>): Array<SelectableValue> {
			if (isPreviousDecisionApproved) {
				return options.filter((o) => o.value !== CoverageRequestTherapyDecision.Trial);
			}

			switch (rating) {
				case CoverageRequestRating.A:
				case CoverageRequestRating.B:
				case CoverageRequestRating.Ab:
					return options.filter((o) => o.value !== CoverageRequestTherapyDecision.Trial);
				default:
					return options;
			}
		}

		function filterDecisionOptionsInNegotiationRound(options: Array<SelectableValue>): Array<SelectableValue> {
			return options.filter((o) => o.value !== CoverageRequestTherapyDecision.Declined);
		}

		return {
			controlType: "typeahead",
			options: this.parent.context.isNegotiable
				? filterDecisionOptionsInNegotiationRound(decisionOptions)
				: filterDecisionOptionsInForm(decisionOptions),
			property: "value",
			selectPropertyValue: true,
		};
	}

	override getValueTypeProps(): Maybe<DataControllerProps> | undefined {
		return {
			controlType: "typeahead",
			property: "value",
			translationPrefix: "decisions-",
		};
	}

	renderBeforeContent(): React.ReactElement {
		return this.parent.context.isEditable ? (
			<CoveragePeriodRelatedEffect cases={this.parent.context.cases} controller={this.parent} />
		) : (
			<></>
		);
	}

	private isPreviouslyApproved = () =>
		this.parent.context.cases
			.flatMap(getCoverageBoundaries)
			.filter(
				(boundaries) =>
					!this.parent.context.flexEntity?.createdOn || boundaries.formCreatedOn < this.parent.context.flexEntity?.createdOn
			)
			.some((boundaries) => boundaries.decision === CoverageRequestTherapyDecision.Approved);
}
